
import React, { useState, useEffect, useRef } from 'react';
import { Property, Owner, PropertyAsset, AssetType, PipelineType, PropertyDocument, DocumentCategory, ChecklistTemplate, Deal, DealStage, Expense, Revenue, PropertyROI, RRRental, CityTaxChannel, CityTaxRule, CityTaxRules, DEFAULT_CITY_TAX_RULES } from '../types';
import {
    Key, MapPin, Building, Search, Plus, ExternalLink, Calendar,
    Shield, Wifi, Zap, Droplets, FileText, CheckSquare,
    BarChart3, Upload, Trash2, Save, AlertCircle, Eye, File, Loader, Tag, Sparkles, Paperclip, Bold, Italic, List, Cloud, Euro, TrendingUp, ChevronLeft, ChevronRight, X, AlertTriangle, Settings, Archive, Receipt, CheckCircle2
} from 'lucide-react';
import { AddExpenseModal } from './modals/AddExpenseModal';
import { AddRevenueModal } from './modals/AddRevenueModal';
import { ExpensesTableView } from './ExpensesTableView';
import { storage } from '../services/storage';
import { aiService } from '../services/aiService';
import { rentalReadyService } from '../services/rentalReadyService';
import { billingService } from '../services/billingService';
import { useModal } from './ui/ModalProvider';

// --- RICH TEXT EDITOR COMPONENT ---
const RichTextEditor: React.FC<{ value: string; onChange: (val: string) => void }> = ({ value, onChange }) => {
    const editorRef = useRef<HTMLDivElement>(null);

    // Sync external value to innerHTML only if not focused (to prevent cursor jump)
    useEffect(() => {
        if (editorRef.current) {
            // Check if active element is the editor itself
            const isFocused = document.activeElement === editorRef.current;
            // Only update if not focused OR if content is totally empty (initial load)
            if (!isFocused || editorRef.current.innerHTML === '') {
                if (editorRef.current.innerHTML !== value) {
                    editorRef.current.innerHTML = value;
                }
            }
        }
    }, [value]);

    const execCmd = (command: string) => {
        document.execCommand(command, false, undefined);
        if (editorRef.current) onChange(editorRef.current.innerHTML);
    };

    return (
        <div className="border border-slate-300 rounded-lg overflow-hidden bg-white flex flex-col h-64">
            <div className="flex items-center space-x-1 p-2 border-b border-slate-200 bg-slate-50">
                <button onClick={() => execCmd('bold')} className="p-1.5 hover:bg-slate-200 rounded text-slate-600" title="Gras"><Bold className="w-4 h-4" /></button>
                <button onClick={() => execCmd('italic')} className="p-1.5 hover:bg-slate-200 rounded text-slate-600" title="Italique"><Italic className="w-4 h-4" /></button>
                <div className="w-px h-4 bg-slate-300 mx-2"></div>
                <button onClick={() => execCmd('insertUnorderedList')} className="p-1.5 hover:bg-slate-200 rounded text-slate-600" title="Liste à puces"><List className="w-4 h-4" /></button>
            </div>
            <div 
                ref={editorRef}
                className="flex-1 p-4 outline-none overflow-y-auto text-sm text-slate-900"
                contentEditable
                suppressContentEditableWarning
                onInput={(e) => onChange(e.currentTarget.innerHTML)} // Live update
                onBlur={(e) => onChange(e.currentTarget.innerHTML)} // Ensure save on blur
            />
        </div>
    );
};

interface ManagedPropertiesProps {
    properties: Property[];
    owners: Owner[];
    deals?: Deal[]; // Added deals prop
    onSaveProperty: (prop: Property) => void;
    initialPropertyId?: string | null;
    onClearInitialId?: () => void;
    onNavigateToProposal?: (dealId: string) => void; // Added nav prop
    financeEnabled?: boolean; // Feature flag for finances
    onRefreshData?: () => void; // Callback pour recharger les données
}

export const ManagedProperties: React.FC<ManagedPropertiesProps> = ({ properties, owners, deals, onSaveProperty, initialPropertyId, onClearInitialId, onNavigateToProposal, financeEnabled = false, onRefreshData }) => {
    const [selectedPropId, setSelectedPropId] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [showArchived, setShowArchived] = useState(false);

    useEffect(() => {
        if (initialPropertyId) {
            setSelectedPropId(initialPropertyId);
            if (onClearInitialId) onClearInitialId();
        }
    }, [initialPropertyId]);

    // Filtrer selon la vue (actifs ou archivés)
    const allManagedProperties = properties.filter(p => p.status === 'MANAGED' || p.status === 'ARCHIVED');
    const managedProperties = showArchived 
        ? allManagedProperties.filter(p => p.status === 'ARCHIVED')
        : allManagedProperties.filter(p => p.status === 'MANAGED');
    
    const filteredProps = managedProperties.filter(p => 
        (p.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
         p.address.toLowerCase().includes(searchTerm.toLowerCase()) || 
         p.city.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const selectedProp = allManagedProperties.find(p => p.id === selectedPropId);
    const selectedOwner = selectedProp ? owners.find(o => o.id === selectedProp.ownerId) : null;
    
    // Find linked deal for selected property
    const linkedDeal = deals && selectedProp 
        ? deals.find(d => d.propertyId === selectedProp.id && d.stage === DealStage.SIGNED) 
        : null;

    if (selectedProp && selectedOwner) {
        return (
            <ManagedPropertyDetail 
                property={selectedProp} 
                owner={selectedOwner}
                linkedDeal={linkedDeal || undefined} 
                onBack={() => setSelectedPropId(null)}
                onSave={onSaveProperty}
                onNavigateToProposal={onNavigateToProposal}
                financeEnabled={financeEnabled}
                onRefreshData={onRefreshData}
            />
        );
    }

    const archivedCount = properties.filter(p => p.status === 'ARCHIVED').length;

    return (
        <div className="p-8 h-full overflow-y-auto">
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h2 className="text-2xl font-bold text-slate-900 flex items-center">
                        <Key className="w-6 h-6 mr-3 text-amber-500" /> 
                        {showArchived ? 'Biens Archivés' : 'Biens en Gestion'}
                    </h2>
                    <p className="text-slate-500">
                        {showArchived 
                            ? 'Biens retirés de la gestion active. Toutes les données restent accessibles.'
                            : 'Parc immobilier sous contrat (Conciergerie & Sous-location).'}
                    </p>
                </div>
                {archivedCount > 0 && (
                    <button
                        onClick={() => setShowArchived(!showArchived)}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                            showArchived
                                ? 'bg-amber-100 text-amber-700 hover:bg-amber-200 border border-amber-300'
                                : 'bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-300'
                        }`}
                    >
                        <Archive className="w-4 h-4" />
                        {showArchived ? 'Voir les actifs' : `Voir les archivés (${archivedCount})`}
                    </button>
                )}
            </div>

            {managedProperties.length === 0 ? (
                <div className="bg-white p-12 rounded-xl text-center border border-dashed border-slate-300">
                    <div className="bg-amber-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Building className="w-8 h-8 text-amber-500" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900">
                        {showArchived ? 'Aucun bien archivé' : 'Aucun bien en gestion'}
                    </h3>
                    <p className="text-slate-500 mt-2">
                        {showArchived 
                            ? 'Aucun bien n\'a été archivé pour le moment.'
                            : 'Signez des contrats dans le Pipeline pour voir apparaître les biens ici.'}
                    </p>
                </div>
            ) : (
                <div className="space-y-4">
                    <div className="relative max-w-md">
                        <Search className="absolute left-3 top-2.5 w-5 h-5 text-slate-400" />
                        <input 
                            type="text" 
                            placeholder="Rechercher par nom, adresse..." 
                            className="pl-10 p-2 border border-slate-300 rounded-lg w-full bg-white text-slate-900"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredProps.map(prop => {
                            const owner = owners.find(o => o.id === prop.ownerId);
                            const isConcierge = prop.contractType === PipelineType.CONCIERGE;
                            
                            return (
                                <div key={prop.id} onClick={() => setSelectedPropId(prop.id!)} className={`bg-white rounded-xl shadow-sm border ${prop.status === 'ARCHIVED' ? 'border-amber-300' : 'border-slate-200'} hover:border-amber-400 hover:shadow-md transition-all cursor-pointer overflow-hidden group`}>
                                    <div className={`h-2 ${prop.status === 'ARCHIVED' ? 'bg-amber-500' : 'bg-slate-900'} group-hover:bg-amber-500 transition-colors`}></div>
                                    <div className="p-6">
                                        <div className="flex justify-between items-start mb-4">
                                            <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 group-hover:bg-amber-100 group-hover:text-amber-600 transition-colors">
                                                <Building className="w-5 h-5" />
                                            </div>
                                            <div className="flex flex-col items-end space-y-1">
                                                {prop.status === 'ARCHIVED' && (
                                                    <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded font-bold border border-amber-200 flex items-center">
                                                        <Archive className="w-3 h-3 mr-1" /> ARCHIVÉ
                                                    </span>
                                                )}
                                                {prop.contractType && (
                                                    <span className={`text-[10px] px-2 py-0.5 rounded font-bold border uppercase tracking-wide ${isConcierge ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-purple-50 text-purple-700 border-purple-200'}`}>
                                                        {isConcierge ? 'Conciergerie' : 'Sous-Location'}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        
                                        {/* Titre (Nom du bien ou Adresse si pas de nom) */}
                                        <h3 className="font-bold text-lg text-slate-900 mb-1 truncate">
                                            {prop.name || prop.address}
                                        </h3>
                                        
                                        {/* Adresse en secondaire si nom présent */}
                                        {prop.name ? (
                                            <p className="text-xs text-slate-400 truncate flex items-center mb-4">
                                                <MapPin className="w-3 h-3 mr-1" /> {prop.address}, {prop.zipCode} {prop.city}
                                            </p>
                                        ) : (
                                            <p className="text-sm text-slate-500 mb-4">{prop.zipCode} {prop.city}</p>
                                        )}
                                        
                                        <div className="flex items-center text-xs text-slate-500 pt-4 border-t border-slate-100">
                                            <span className="font-semibold text-slate-700 mr-1">{owner?.firstName} {owner?.lastName}</span>
                                            <span>• {prop.type}</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

// --- DETAIL COMPONENT ---

const ManagedPropertyDetail: React.FC<{ 
    property: Property; 
    owner: Owner; 
    linkedDeal?: Deal;
    onBack: () => void; 
    onSave: (p: Property) => void;
    onNavigateToProposal?: (dealId: string) => void;
    financeEnabled?: boolean;
    onRefreshData?: () => void;
}> = ({ property, owner, linkedDeal, onBack, onSave, onNavigateToProposal, financeEnabled = false, onRefreshData }) => {
    const { toast, confirm, prompt, choice } = useModal();
    const [activeTab, setActiveTab] = useState<'overview' | 'assets' | 'documents' | 'checklist' | 'finances' | 'settings'>('overview');
    
    // Local state for edits
    const [propertyName, setPropertyName] = useState(property.name || '');
    const [internalNotes, setInternalNotes] = useState(property.internalNotes || '');
    const [assets, setAssets] = useState<PropertyAsset[]>(property.assets || []);
    const [documents, setDocuments] = useState<PropertyDocument[]>(property.documents || []);
    
    // AI Name Generation State
    const [aiNameSuggestions, setAiNameSuggestions] = useState<string[]>([]);
    
    // Dynamic Configuration State
    const [docCategories, setDocCategories] = useState<DocumentCategory[]>([]);
    const [checklistTemplates, setChecklistTemplates] = useState<ChecklistTemplate[]>([]);
    const [checklistState, setChecklistState] = useState<Record<string, boolean>>(property.checklist || {});

    const [isGeneratingName, setIsGeneratingName] = useState(false);
    const [docCategory, setDocCategory] = useState('');
    const [isUploading, setIsUploading] = useState(false);

    // Auto-save Status
    const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'modified'>('saved');
    const isMounted = useRef(false);


    useEffect(() => {
        const loadConfig = async () => {
            const docs = await storage.getDocumentCategories();
            setDocCategories(docs);
            if (docs.length > 0) setDocCategory(docs[0].name);

            const checks = await storage.getChecklistTemplates();
            // Filter by contract type
            const filteredChecks = checks.filter(c => c.contract_type === property.contractType);
            setChecklistTemplates(filteredChecks);
        };
        loadConfig();
    }, [property.contractType]);

    // --- AUTO SAVE LOGIC ---
    useEffect(() => {
        if (!isMounted.current) {
            isMounted.current = true;
            return;
        }

        setSaveStatus('modified');
        
        const timer = setTimeout(() => {
            setSaveStatus('saving');
            onSave({
                ...property,
                name: propertyName, 
                internalNotes,
                assets,
                checklist: checklistState,
                documents
            });
            setTimeout(() => setSaveStatus('saved'), 800); // Small delay for UX
        }, 1500); // 1.5s debounce

        return () => clearTimeout(timer);
    }, [propertyName, internalNotes, assets, checklistState, documents]); // Trigger on any field change

    // Synchroniser les assets vers les dépenses récurrentes quand ils changent
    useEffect(() => {
        if (property.id && assets.length > 0) {
            // Délai pour éviter trop d'appels pendant l'édition
            const timer = setTimeout(async () => {
                try {
                    await storage.syncAssetsToExpenses(property.id!);
                } catch (error) {
                    console.error('Error syncing assets to expenses:', error);
                }
            }, 2000); // 2s après le dernier changement
            
            return () => clearTimeout(timer);
        }
    }, [assets, property.id]);

    // AI Generation
    const generateName = async () => {
        setIsGeneratingName(true);
        setAiNameSuggestions([]);
        try {
            const names = await aiService.generatePropertyName(
                property.type,
                property.city,
                property.surface,
                property.bedrooms || 0
            );
            
            if (names && names.length > 0) {
                setAiNameSuggestions(names);
            }
        } catch (e: any) {
            console.error("AI Generation Error", e);
            toast.error("Erreur lors de la génération du nom: " + (e.message || "Erreur inconnue"));
        } finally {
            setIsGeneratingName(false);
        }
    };

    const selectName = (name: string) => {
        setPropertyName(name);
        setAiNameSuggestions([]);
        toast.success("Nom sélectionné !");
    };

    // Asset Management
    const addAsset = () => {
        const newAsset: PropertyAsset = {
            id: Date.now().toString(),
            type: 'INTERNET',
            provider: '',
            contractNumber: '',
            startDate: new Date().toISOString().split('T')[0],
            status: 'ACTIVE'
        };
        setAssets([...assets, newAsset]);
    };

    const updateAsset = (id: string, field: keyof PropertyAsset, value: any) => {
        setAssets(assets.map(a => a.id === id ? { ...a, [field]: value } : a));
    };

    const removeAsset = async (id: string) => {
        if (await confirm({ title: "Supprimer le contrat", message: "Voulez-vous supprimer ce contrat ?" })) {
            setAssets(assets.filter(a => a.id !== id));
        }
    };

    // Helper: Map Asset Type to Document Category
    const getCategoryForAsset = (assetType: AssetType): string => {
        switch(assetType) {
            case 'INSURANCE': return 'Assurance Habitation';
            case 'ELECTRICITY': 
            case 'WATER':
            case 'GAS': return 'Facture Eau/Elec';
            case 'INTERNET': return 'Autre'; // Or create a specific category
            default: return 'Autre';
        }
    };

    const handleAssetDocUpload = async (e: React.ChangeEvent<HTMLInputElement>, assetId: string, assetType: AssetType) => {
        if (e.target.files && e.target.files[0]) {
            try {
                const file = e.target.files[0];
                const fileName = await storage.uploadPropertyDocument(file, property.id!);
                
                // 1. Create Document with PATH (secure)
                const category = getCategoryForAsset(assetType);
                const newDoc: PropertyDocument = {
                    id: Date.now().toString(),
                    name: `Contrat ${assetType} - ${file.name}`,
                    type: file.type.split('/')[1] || 'file',
                    category: category,
                    uploadDate: new Date().toISOString(),
                    path: fileName // NEW: Store path instead of URL
                };

                // 2. Add to Documents List
                const updatedDocs = [...documents, newDoc];
                setDocuments(updatedDocs);

                // 3. Link Document to Asset
                setAssets(prev => prev.map(a => a.id === assetId ? { ...a, documentId: newDoc.id } : a));

                toast.success("Document lié et ajouté à la bibliothèque.");

            } catch (err) {
                console.error(err);
                toast.error("Erreur lors de l'upload.");
            }
        }
    };

    // General Document Management
    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setIsUploading(true);
            try {
                const file = e.target.files[0];
                // Upload returns the path now
                const fileName = await storage.uploadPropertyDocument(file, property.id!);
                
                const newDoc: PropertyDocument = {
                    id: Date.now().toString(),
                    name: file.name,
                    type: file.type.split('/')[1] || 'file',
                    category: docCategory || 'Autre', 
                    uploadDate: new Date().toISOString(),
                    path: fileName // NEW: Store path
                };

                const updatedDocs = [...documents, newDoc];
                // Triggers auto-save
                setDocuments(updatedDocs);
                
                toast.success("Fichier envoyé !");

            } catch (err) {
                console.error(err);
                toast.error("Erreur lors de l'upload du fichier.");
            } finally {
                setIsUploading(false);
            }
        }
    };

    // NEW: Handle Secure View
    const handleViewDocument = async (doc: PropertyDocument) => {
        if (doc.path) {
            // New Secure System: Request Fresh URL
            const freshUrl = await storage.getFileUrl(doc.path);
            if (freshUrl) {
                window.open(freshUrl, '_blank');
            } else {
                toast.error("Impossible de générer le lien de téléchargement.");
            }
        } else if (doc.url) {
            // Legacy System (Fallback)
            window.open(doc.url, '_blank');
        } else {
            toast.error("Fichier introuvable.");
        }
    };

    const deleteDocument = async (docId: string) => {
        const docToDelete = documents.find(d => d.id === docId);
        
        if(await confirm({ title: "Suppression", message: "Supprimer ce document de la liste et du stockage ?", danger: true })) {
            
            // 1. Delete from Storage if path exists
            if (docToDelete && docToDelete.path) {
                const { error } = await storage.deletePropertyDocument(docToDelete.path);
                if (error) {
                    console.error("Storage delete error:", error);
                    toast.error("Erreur lors de la suppression du fichier sur le serveur.");
                    // We might still want to remove metadata, but safer to stop? 
                    // Let's continue to keep UI clean, but log error.
                }
            }

            // 2. Remove from Local State
            const updatedDocs = documents.filter(d => d.id !== docId);
            setDocuments(updatedDocs);
            
            // 3. Remove link from assets if exists - triggers auto-save
            setAssets(prev => prev.map(a => a.documentId === docId ? { ...a, documentId: undefined } : a));
            
            toast.info("Document supprimé.");
        }
    };

    // Checklist Management
    const toggleChecklistItem = (templateId: string) => {
        setChecklistState(prev => ({
            ...prev,
            [templateId]: !prev[templateId]
        }));
    };

    // Property Archive Handler
    const handleArchiveProperty = async (propertyToArchive: Property) => {
        if (!propertyToArchive.id) {
            toast.error("Impossible d'archiver : ID du bien manquant.");
            return;
        }

        try {
            const propertyDisplayName = propertyToArchive.name || propertyToArchive.address || 'ce bien';
            
            // Confirmation avec explication
            const confirmed = await confirm({
                title: "📦 Archiver le bien",
                message: `Voulez-vous archiver "${propertyDisplayName}" ?\n\nCette action retirera le bien de la gestion active, mais toutes les données (documents, finances, dépenses, revenus) resteront accessibles.\n\nLe bien pourra toujours être consulté depuis les deals, propositions et contrats associés.`,
                confirmText: "Oui, archiver",
                cancelText: "Annuler",
                danger: false
            });

            if (!confirmed) {
                return;
            }

            // Archiver le bien
            const result = await storage.removeManagedStatus(propertyToArchive.id);
            
            if (result.error) {
                toast.error(`Erreur lors de l'archivage : ${result.error.message || 'Erreur inconnue'}`);
                return;
            }

            toast.success(`Le bien "${propertyDisplayName}" a été archivé avec succès.`);
            
            // Recharger les données si callback fourni
            if (onRefreshData) {
                await onRefreshData();
            }
            
            // Rediriger vers la liste
            onBack();
        } catch (error: any) {
            console.error("Error archiving property:", error);
            toast.error(`Erreur lors de l'archivage : ${error.message || 'Erreur inconnue'}`);
        }
    };

    // Property Deletion Handler
    const handleDeleteProperty = async (propertyToDelete: Property) => {
        if (!propertyToDelete.id) {
            toast.error("Impossible de supprimer : ID du bien manquant.");
            return;
        }

        try {
            // Étape 1 : Charger les impacts
            const impact = await storage.getPropertyDeletionImpact(propertyToDelete.id);
            
            if (impact.error) {
                toast.error("Erreur lors de l'évaluation des impacts.");
                return;
            }

            // Construire le message d'impact avec formatage clair
            const impactItems: string[] = [];
            if (impact.dealsCount > 0) {
                impactItems.push(`⚠️ ${impact.dealsCount} deal${impact.dealsCount > 1 ? 's' : ''} lié${impact.dealsCount > 1 ? 's' : ''} (seront conservés mais sans lien au bien)`);
            }
            if (impact.contractsCount > 0) {
                impactItems.push(`📋 ${impact.contractsCount} contrat${impact.contractsCount > 1 ? 's' : ''} (seront conservés mais sans lien au bien)`);
            }
            if (impact.proposalsCount > 0) {
                impactItems.push(`📝 ${impact.proposalsCount} proposition${impact.proposalsCount > 1 ? 's' : ''} commerciale${impact.proposalsCount > 1 ? 's' : ''} (seront conservées mais sans lien au bien)`);
            }
            if (impact.documentsCount > 0) {
                impactItems.push(`📄 ${impact.documentsCount} document${impact.documentsCount > 1 ? 's' : ''} (seront supprimés)`);
            }
            if (impact.expensesCount > 0) {
                impactItems.push(`💰 ${impact.expensesCount} dépense${impact.expensesCount > 1 ? 's' : ''} (seront supprimées)`);
            }
            if (impact.revenueCount > 0) {
                impactItems.push(`💵 ${impact.revenueCount} enregistrement${impact.revenueCount > 1 ? 's' : ''} de revenus (seront supprimés)`);
            }

            // Formatage du message avec sauts de ligne
            const propertyDisplayName = propertyToDelete.name || propertyToDelete.address || 'ce bien';
            let impactMessage = `Êtes-vous sûr de vouloir supprimer "${propertyDisplayName}" ?\n\nCette action est irréversible et supprimera définitivement toutes les données associées.`;
            
            if (impactItems.length > 0) {
                impactMessage += `\n\nCette action affectera :\n\n${impactItems.map(item => `• ${item}`).join('\n')}`;
            } else {
                impactMessage += `\n\nAucune donnée liée trouvée.`;
            }

            // Étape 2 : Première confirmation avec affichage des impacts
            const firstConfirm = await confirm({
                title: "⚠️ Supprimer le bien",
                message: impactMessage,
                confirmText: "Continuer",
                cancelText: "Annuler",
                danger: true
            });

            if (!firstConfirm) {
                return;
            }

            // Étape 3 : Confirmation finale avec saisie du nom
            const expectedName = propertyDisplayName;
            const typedName = await prompt({
                title: "⚠️ Confirmation finale",
                message: `Pour confirmer la suppression, tapez le nom du bien :`,
                placeholder: expectedName,
                confirmText: "Supprimer définitivement",
                cancelText: "Annuler",
                expectedValue: expectedName
            });

            if (!typedName || typedName.trim() !== expectedName) {
                if (typedName !== null) {
                    toast.error("Le nom saisi ne correspond pas. Suppression annulée.");
                }
                return;
            }

            // Étape 4 : Supprimer le bien
            const result = await storage.deleteProperty(propertyToDelete.id);

            if (result.error) {
                toast.error(`Erreur lors de la suppression : ${result.error.message || 'Erreur inconnue'}`);
                return;
            }

            // Succès
            toast.success(`Bien "${propertyDisplayName}" supprimé avec succès.`);
            
            // Recharger les données si callback fourni
            if (onRefreshData) {
                await onRefreshData();
            }
            
            // Rediriger vers la liste
            onBack();
        } catch (error: any) {
            console.error("Error deleting property:", error);
            toast.error(`Erreur lors de la suppression : ${error.message || 'Erreur inconnue'}`);
        }
    };

    const isConcierge = property.contractType === PipelineType.CONCIERGE;

    return (
        <div className="p-8 h-full overflow-y-auto bg-slate-50">
            {/* Header */}
            <div className="flex items-center justify-between mb-6 sticky top-0 z-10 bg-slate-50 pb-2">
                <button onClick={onBack} className="text-slate-500 hover:text-slate-800 flex items-center font-medium">
                    ← Retour à la liste
                </button>
                <div className="flex items-center space-x-3">
                    {/* Auto-Save Indicator */}
                    <div className="flex items-center text-xs font-medium bg-white px-3 py-1.5 rounded-full shadow-sm border border-slate-200">
                        {saveStatus === 'saving' ? (
                            <>
                                <Loader className="w-3 h-3 text-amber-500 animate-spin mr-2" />
                                <span className="text-slate-500">Enregistrement...</span>
                            </>
                        ) : saveStatus === 'modified' ? (
                            <>
                                <Cloud className="w-3 h-3 text-slate-400 mr-2" />
                                <span className="text-slate-400">Modifications en cours...</span>
                            </>
                        ) : (
                            <>
                                <Cloud className="w-3 h-3 text-green-500 mr-2" />
                                <span className="text-green-600">Enregistré</span>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
                <div className="flex justify-between items-start">
                    <div className="flex-1 mr-8">
                        <div className="flex items-center space-x-3 mb-2">
                            {/* Input Name */}
                            <input 
                                type="text" 
                                className="text-2xl font-bold text-slate-900 border-b border-transparent hover:border-slate-300 focus:border-amber-500 focus:outline-none bg-transparent w-full transition-colors placeholder-slate-400"
                                placeholder="Nom du bien (ex: Le Cocon Urbain)"
                                value={propertyName}
                                onChange={(e) => setPropertyName(e.target.value)}
                            />
                            
                            {/* AI Gen Button */}
                            <button 
                                onClick={generateName} 
                                disabled={isGeneratingName}
                                className="p-2 bg-amber-50 text-amber-600 rounded-lg hover:bg-amber-100 transition-colors shrink-0"
                                title="Générer 3 propositions de nom avec l'IA"
                            >
                                {isGeneratingName ? <Loader className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
                            </button>

                            {property.contractType && (
                                <span className={`text-xs px-2 py-0.5 rounded font-bold border uppercase tracking-wide shrink-0 ${isConcierge ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-purple-50 text-purple-700 border-purple-200'}`}>
                                    {isConcierge ? 'Conciergerie' : 'Sous-Location'}
                                </span>
                            )}
                        </div>
                        
                        {/* AI Name Suggestions */}
                        {aiNameSuggestions.length > 0 && (
                            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                <p className="text-sm font-bold text-blue-900 mb-3">💡 3 propositions générées par l'IA :</p>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                    {aiNameSuggestions.map((name, index) => (
                                        <button
                                            key={index}
                                            onClick={() => selectName(name)}
                                            className="p-3 bg-white border-2 border-blue-200 hover:border-blue-400 hover:bg-blue-50 rounded-lg text-left transition-all group"
                                        >
                                            <div className="flex items-center justify-between">
                                                <span className="font-medium text-slate-900 group-hover:text-blue-700">{name}</span>
                                                <span className="text-xs text-blue-600 font-bold opacity-0 group-hover:opacity-100 transition-opacity">✓ Sélectionner</span>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        
                        <p className="text-slate-500 flex items-center mt-1">
                            <MapPin className="w-4 h-4 mr-1" /> {property.address}, {property.zipCode} {property.city}
                        </p>
                    </div>
                    <div className="text-right shrink-0">
                         <p className="text-sm font-bold text-slate-900">{owner.firstName} {owner.lastName}</p>
                         <p className="text-xs text-slate-500">{owner.email}</p>
                         <p className="text-xs text-slate-500">{owner.phone}</p>
                    </div>
                </div>

            </div>

            {/* Indicateur bien archivé */}
            {property.status === 'ARCHIVED' && (
                <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4 mb-6 flex items-start gap-3">
                    <Archive className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <h3 className="font-bold text-amber-900 mb-1">Ce bien est archivé</h3>
                        <p className="text-sm text-amber-800">Toutes les données (documents, finances, etc.) restent accessibles. Le bien n'apparaît plus dans la liste principale mais peut être consulté depuis les deals, propositions et contrats associés.</p>
                    </div>
                </div>
            )}

            {/* Tabs */}
            <div className="flex space-x-1 mb-6 bg-slate-200 p-1 rounded-lg w-fit">
                <button onClick={() => setActiveTab('overview')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'overview' ? 'bg-white text-slate-900 shadow' : 'text-slate-600 hover:text-slate-800'}`}>Vue d'ensemble</button>
                <button onClick={() => setActiveTab('checklist')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'checklist' ? 'bg-white text-slate-900 shadow' : 'text-slate-600 hover:text-slate-800'}`}>Onboarding</button>
                <button onClick={() => setActiveTab('assets')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'assets' ? 'bg-white text-slate-900 shadow' : 'text-slate-600 hover:text-slate-800'}`}>Contrats & Fournisseurs</button>
                <button onClick={() => setActiveTab('documents')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'documents' ? 'bg-white text-slate-900 shadow' : 'text-slate-600 hover:text-slate-800'}`}>Documents</button>
                {financeEnabled && (
                    <button onClick={() => setActiveTab('finances')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'finances' ? 'bg-white text-slate-900 shadow' : 'text-slate-600 hover:text-slate-800'}`}>Finances</button>
                )}
                <button onClick={() => setActiveTab('settings')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'settings' ? 'bg-white text-slate-900 shadow' : 'text-slate-600 hover:text-slate-800'}`}>Paramètres</button>
            </div>

            {/* CONTENT */}
            
            {activeTab === 'overview' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Left Column */}
                    <div className="flex flex-col gap-6">
                        {/* View Proposal Button - MOVED HERE OUTSIDE OF THE BLOCK */}
                        {linkedDeal?.proposalId && onNavigateToProposal && (
                            <button 
                                onClick={() => onNavigateToProposal(linkedDeal.id!)}
                                className="w-full bg-white text-amber-600 px-4 py-3 rounded-xl flex items-center justify-center shadow-sm hover:bg-amber-50 transition-colors border border-amber-200 font-bold"
                            >
                                <FileText className="w-4 h-4 mr-2" /> Voir la Proposition Commerciale
                            </button>
                        )}
                    </div>

                    <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 h-fit">
                        <h3 className="font-bold text-lg mb-4 flex items-center"><FileText className="w-5 h-5 mr-2 text-slate-400" /> Notes Internes</h3>
                        {/* REPLACEMENT: Rich Text Editor */}
                        <RichTextEditor 
                            value={internalNotes} 
                            onChange={(val) => setInternalNotes(val)}
                        />
                    </div>
                </div>
            )}

            {activeTab === 'checklist' && (
                <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 max-w-2xl">
                     <h3 className="font-bold text-lg mb-6 flex items-center">
                         <CheckSquare className="w-5 h-5 mr-2 text-slate-400" /> Checklist de Démarrage 
                         <span className="ml-2 text-xs bg-slate-100 text-slate-500 px-2 py-1 rounded border border-slate-200">{property.contractType || 'Standard'}</span>
                     </h3>
                     
                     <div className="space-y-4">
                         {checklistTemplates.length === 0 && (
                             <p className="text-slate-400 italic text-sm">Aucune étape configurée dans l'administration pour ce type de contrat.</p>
                         )}
                         {checklistTemplates.map(item => (
                             <div key={item.id} className="flex items-center justify-between p-3 border border-slate-100 rounded-lg hover:bg-slate-50 transition-colors">
                                 <label className="flex items-center cursor-pointer flex-1">
                                     <input 
                                        type="checkbox" 
                                        className="w-5 h-5 text-amber-500 rounded focus:ring-amber-500 border-gray-300 mr-3"
                                        checked={!!checklistState[item.id]}
                                        onChange={() => toggleChecklistItem(item.id)}
                                     />
                                     <span className={checklistState[item.id] ? 'text-slate-400 line-through' : 'text-slate-800 font-medium'}>{item.label}</span>
                                 </label>
                                 
                                 {/* Partner Link Button */}
                                 {item.link_url && (
                                     <a 
                                        href={item.link_url} 
                                        target="_blank" 
                                        rel="noreferrer"
                                        className="ml-4 bg-green-50 text-green-700 hover:bg-green-100 border border-green-200 px-3 py-1.5 rounded-lg text-xs font-bold flex items-center transition-colors shadow-sm"
                                     >
                                         <ExternalLink className="w-3 h-3 mr-1.5" />
                                         {item.link_label || 'Voir l\'offre'}
                                     </a>
                                 )}
                             </div>
                         ))}
                     </div>
                </div>
            )}

            {activeTab === 'assets' && (
                <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                    <div className="flex justify-between items-center mb-6">
                        <h3 className="font-bold text-lg flex items-center"><Shield className="w-5 h-5 mr-2 text-slate-400" /> Abonnements & Contrats</h3>
                        <button onClick={addAsset} className="text-xs bg-slate-900 text-white px-3 py-1.5 rounded hover:bg-slate-800 flex items-center">
                            <Plus className="w-3 h-3 mr-1" /> Ajouter
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {assets.map(asset => {
                            const linkedDoc = asset.documentId ? documents.find(d => d.id === asset.documentId) : null;
                            
                            return (
                            <div key={asset.id} className="border border-slate-200 rounded-lg p-4 bg-slate-50/50">
                                <div className="flex justify-between items-start mb-3">
                                    <div className="flex items-center">
                                        <div className="p-2 bg-white rounded border border-slate-200 mr-3 text-slate-500">
                                            {asset.type === 'INSURANCE' && <Shield className="w-5 h-5" />}
                                            {asset.type === 'INTERNET' && <Wifi className="w-5 h-5" />}
                                            {asset.type === 'ELECTRICITY' && <Zap className="w-5 h-5" />}
                                            {asset.type === 'WATER' && <Droplets className="w-5 h-5" />}
                                            {asset.type === 'OTHER' && <FileText className="w-5 h-5" />}
                                        </div>
                                        <select 
                                            className="font-bold text-sm bg-transparent border-none p-0 focus:ring-0 text-slate-800"
                                            value={asset.type}
                                            onChange={(e) => updateAsset(asset.id, 'type', e.target.value)}
                                        >
                                            <option value="INSURANCE">Assurance</option>
                                            <option value="INTERNET">Internet</option>
                                            <option value="ELECTRICITY">Électricité</option>
                                            <option value="WATER">Eau</option>
                                            <option value="OTHER">Autre</option>
                                        </select>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        {/* UPLOAD BUTTON */}
                                        <div className="relative group">
                                            {linkedDoc ? (
                                                <button 
                                                    onClick={() => handleViewDocument(linkedDoc)}
                                                    className="p-1.5 text-blue-500 hover:bg-blue-50 rounded flex items-center"
                                                    title="Voir le document lié"
                                                >
                                                    <FileText className="w-4 h-4" />
                                                </button>
                                            ) : (
                                                <label className="p-1.5 text-slate-300 hover:text-slate-600 hover:bg-slate-100 rounded cursor-pointer" title="Lier un document">
                                                    <Paperclip className="w-4 h-4" />
                                                    <input 
                                                        type="file" 
                                                        className="hidden" 
                                                        onChange={(e) => handleAssetDocUpload(e, asset.id, asset.type)}
                                                    />
                                                </label>
                                            )}
                                        </div>
                                        <button onClick={() => removeAsset(asset.id)} className="text-slate-300 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                                    </div>
                                </div>
                                
                                <div className="space-y-2">
                                    <input 
                                        type="text" 
                                        placeholder="Fournisseur (ex: Orange)" 
                                        className="w-full p-1.5 text-sm border rounded bg-white text-slate-900"
                                        value={asset.provider}
                                        onChange={(e) => updateAsset(asset.id, 'provider', e.target.value)}
                                    />
                                    <input 
                                        type="text" 
                                        placeholder="N° Contrat" 
                                        className="w-full p-1.5 text-sm border rounded bg-white text-slate-900"
                                        value={asset.contractNumber}
                                        onChange={(e) => updateAsset(asset.id, 'contractNumber', e.target.value)}
                                    />
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <label className="text-[10px] text-slate-400 uppercase font-bold">Début</label>
                                            <input type="date" className="w-full p-1.5 text-xs border rounded bg-white text-slate-900" value={asset.startDate} onChange={(e) => updateAsset(asset.id, 'startDate', e.target.value)} />
                                        </div>
                                        <div>
                                            <label className="text-[10px] text-slate-400 uppercase font-bold">Fin / Échéance</label>
                                            <input type="date" className="w-full p-1.5 text-xs border rounded bg-white text-slate-900" value={asset.endDate || ''} onChange={(e) => updateAsset(asset.id, 'endDate', e.target.value)} />
                                        </div>
                                    </div>
                                    <div>
                                         <label className="text-[10px] text-slate-400 uppercase font-bold">Coût mensuel</label>
                                         <div className="relative">
                                            <input type="number" className="w-full p-1.5 text-sm border rounded pl-6 bg-white text-slate-900" value={asset.monthlyCost || ''} onChange={(e) => updateAsset(asset.id, 'monthlyCost', Number(e.target.value))} />
                                            <span className="absolute left-2 top-1.5 text-slate-400 text-xs">€</span>
                                         </div>
                                    </div>
                                </div>
                            </div>
                        )})}
                        {assets.length === 0 && <p className="text-sm text-slate-400 italic">Aucun contrat enregistré.</p>}
                    </div>
                </div>
            )}

             {activeTab === 'documents' && (
                <div className="bg-white p-6 rounded-xl border border-slate-200">
                    <h3 className="font-bold text-lg mb-4 flex items-center">
                        <FileText className="w-5 h-5 mr-2 text-slate-400" /> Documents du bien
                    </h3>
                    
                    {/* Upload Zone */}
                    <div className="mb-6 space-y-3">
                        <label className="block text-sm font-medium text-slate-700">Type de document</label>
                        <div className="flex gap-4 items-start">
                            {docCategories.length > 0 ? (
                                <select 
                                    className="p-2 border border-slate-300 rounded-lg bg-white text-sm focus:ring-amber-500 focus:border-amber-500 text-slate-900"
                                    value={docCategory}
                                    onChange={(e) => setDocCategory(e.target.value)}
                                >
                                    {docCategories.map(cat => (
                                        <option key={cat.id} value={cat.name}>{cat.name}</option>
                                    ))}
                                </select>
                            ) : (
                                <p className="text-sm text-red-500 self-center">Aucune catégorie définie (voir Admin).</p>
                            )}

                            <div className="flex-1 border-2 border-dashed border-slate-300 rounded-xl p-8 text-center bg-slate-50 hover:bg-slate-100 transition-colors relative">
                                {isUploading ? (
                                    <div className="flex flex-col items-center justify-center py-2">
                                        <Loader className="w-8 h-8 text-amber-500 animate-spin mb-3" />
                                        <span className="text-sm text-slate-500 font-medium">Envoi en cours...</span>
                                    </div>
                                ) : (
                                    <>
                                        <input 
                                            type="file" 
                                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" 
                                            onChange={handleFileUpload}
                                            accept="image/*,application/pdf"
                                            disabled={docCategories.length === 0}
                                        />
                                        <div className="bg-white w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-2 shadow-sm">
                                            <Upload className="w-5 h-5 text-amber-500" />
                                        </div>
                                        <h4 className="font-bold text-slate-800 text-sm">Ajouter un document</h4>
                                        <p className="text-xs text-slate-400 mt-1">PDF, JPG, PNG (Max 5Mo)</p>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* File List */}
                    <div className="space-y-2">
                        {documents.length > 0 ? (
                            documents.map(doc => (
                                <div key={doc.id} className="flex items-center justify-between p-3 bg-white border border-slate-100 rounded-lg hover:shadow-sm transition-shadow group">
                                    <div className="flex items-center overflow-hidden">
                                        <div className="w-10 h-10 rounded bg-slate-100 flex items-center justify-center text-slate-500 mr-3 shrink-0">
                                            {doc.type.includes('pdf') ? <FileText className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-sm font-medium text-slate-700 truncate">{doc.name}</p>
                                            <div className="flex items-center mt-0.5 space-x-2">
                                                {doc.category && (
                                                    <span className="text-[10px] font-bold uppercase bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded border border-slate-200">
                                                        {doc.category}
                                                    </span>
                                                )}
                                                <span className="text-[10px] text-slate-400">{new Date(doc.uploadDate).toLocaleDateString()}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <button 
                                            onClick={() => handleViewDocument(doc)} 
                                            className="p-1.5 text-slate-400 hover:text-blue-500 hover:bg-blue-50 rounded"
                                            title="Voir / Télécharger"
                                        >
                                            <ExternalLink className="w-4 h-4" />
                                        </button>
                                        <button 
                                            onClick={() => deleteDocument(doc.id)} 
                                            className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded"
                                            title="Supprimer"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p className="text-sm text-slate-400 italic text-center py-4">Aucun document pour le moment.</p>
                        )}
                    </div>
                </div>
            )}

            {activeTab === 'finances' && (
                <PropertyFinancesTab 
                    property={property}
                    onSaveExpense={async (expense) => {
                        await storage.saveExpense(expense);
                        toast.success('Dépense enregistrée');
                    }}
                    onSaveRevenue={async (revenue) => {
                        await storage.saveRevenue(revenue);
                        toast.success('Revenus enregistrés');
                    }}
                />
            )}

            {activeTab === 'settings' && (
                <div className="space-y-6">
                    {/* Facturation propriétaire (Conciergerie uniquement) */}
                    {property.contractType === PipelineType.CONCIERGE && (
                        <PropertyBillingSettings property={property} onSave={onSave} />
                    )}

                    {/* Retirer de la gestion */}
                    {property.status === 'MANAGED' && (
                        <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-6">
                            <h3 className="font-bold text-lg text-amber-900 mb-2 flex items-center">
                                <Archive className="w-5 h-5 mr-2" />
                                Retirer de la gestion
                            </h3>
                            <p className="text-sm text-amber-800 mb-4">
                                Cette action archive le bien. Toutes les données (documents, finances, dépenses, revenus) restent accessibles. Le bien pourra toujours être consulté depuis les deals, propositions et contrats associés, mais n'apparaîtra plus dans la liste principale des biens en gestion.
                            </p>
                            <button 
                                onClick={() => handleArchiveProperty(property)}
                                className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg flex items-center font-medium transition-colors"
                            >
                                <Archive className="w-4 h-4 mr-2" />
                                Archiver ce bien
                            </button>
                        </div>
                    )}

                    {/* Supprimer le bien */}
                    <div className="bg-red-50 border-2 border-red-300 rounded-xl p-6">
                        <h3 className="font-bold text-lg text-red-900 mb-2 flex items-center">
                            <AlertTriangle className="w-5 h-5 mr-2" />
                            Supprimer le bien
                        </h3>
                        <p className="text-sm text-red-800 mb-4">
                            Cette action supprime définitivement le bien et toutes ses données associées (documents, dépenses, revenus). Les deals, contrats et propositions liés seront conservés mais perdront leur référence au bien. Cette action est irréversible.
                        </p>
                        <button 
                            onClick={() => handleDeleteProperty(property)}
                            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center font-medium transition-colors"
                        >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Supprimer définitivement ce bien
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

// Component for the owner billing settings card (Conciergerie only)
const PropertyBillingSettings: React.FC<{
    property: Property;
    onSave: (p: Property) => void;
}> = ({ property, onSave }) => {
    const { toast } = useModal();
    const [rentals, setRentals] = useState<RRRental[]>([]);
    const [rentalsLoading, setRentalsLoading] = useState(false);
    const [rentalsError, setRentalsError] = useState<string | null>(null);
    const [rentalsLoaded, setRentalsLoaded] = useState(false);
    const [contractRate, setContractRate] = useState<number | null>(null);
    const [overrideInput, setOverrideInput] = useState(
        property.commissionRateOverride != null ? String(property.commissionRateOverride) : ''
    );

    // Taux hérité du contrat signé (affiché en placeholder)
    useEffect(() => {
        if (property.id) {
            billingService.getContractCommissionRate(property.id).then(setContractRate);
        }
    }, [property.id]);

    const loadRentals = async () => {
        if (rentalsLoaded || rentalsLoading || !property.agencyId) return;
        setRentalsLoading(true);
        setRentalsError(null);
        try {
            const list = await rentalReadyService.listRentals(property.agencyId);
            setRentals(list);
            setRentalsLoaded(true);
        } catch (e: any) {
            setRentalsError(e.message || 'Erreur lors du chargement des biens RentalReady');
        } finally {
            setRentalsLoading(false);
        }
    };

    const handleLinkRental = (rentalId: string) => {
        if (!rentalId) {
            onSave({ ...property, rrRentalId: null, rrRentalName: null });
            toast.success('Bien délié de RentalReady');
            return;
        }
        const rental = rentals.find(r => r.id === rentalId);
        onSave({ ...property, rrRentalId: rentalId, rrRentalName: rental?.name || rentalId });
        toast.success(`Bien lié à « ${rental?.name || rentalId} »`);
    };

    const handleOverrideBlur = () => {
        const trimmed = overrideInput.trim();
        if (trimmed === '') {
            if (property.commissionRateOverride != null) {
                onSave({ ...property, commissionRateOverride: null });
                toast.success('Taux personnalisé supprimé : le taux du contrat s\'applique');
            }
            return;
        }
        const value = parseFloat(trimmed.replace(',', '.'));
        if (isNaN(value) || value < 0 || value > 100) {
            toast.error('Le taux doit être un pourcentage entre 0 et 100');
            setOverrideInput(property.commissionRateOverride != null ? String(property.commissionRateOverride) : '');
            return;
        }
        if (value !== property.commissionRateOverride) {
            onSave({ ...property, commissionRateOverride: value });
            toast.success(`Taux personnalisé enregistré : ${value} %`);
        }
    };

    const effectiveRate = property.commissionRateOverride ?? contractRate;

    return (
        <div className="bg-white border-2 border-emerald-200 rounded-xl p-6">
            <h3 className="font-bold text-lg text-slate-900 mb-1 flex items-center">
                <Receipt className="w-5 h-5 mr-2 text-emerald-600" />
                Facturation propriétaire
            </h3>
            <p className="text-sm text-slate-500 mb-4">
                Lien RentalReady et paramètres utilisés pour la génération du CRG mensuel.
            </p>

            <div className="space-y-4">
                {/* Lien RentalReady */}
                <div>
                    <label className="block text-sm font-bold text-slate-700 mb-1">Bien RentalReady lié</label>
                    {property.rrRentalId && (
                        <div className="flex items-center gap-2 mb-2">
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-bold">
                                <CheckCircle2 className="w-3 h-3" />
                                Lié : {property.rrRentalName || property.rrRentalId}
                            </span>
                            <button
                                type="button"
                                onClick={() => handleLinkRental('')}
                                className="text-xs text-red-500 hover:text-red-700 font-medium"
                            >
                                Délier
                            </button>
                        </div>
                    )}
                    <select
                        className="w-full p-2 border border-slate-300 rounded-lg bg-white text-slate-900 text-sm"
                        value={property.rrRentalId || ''}
                        onFocus={loadRentals}
                        onChange={e => handleLinkRental(e.target.value)}
                        disabled={rentalsLoading}
                    >
                        <option value="">
                            {rentalsLoading
                                ? 'Chargement des biens RentalReady...'
                                : rentalsLoaded
                                    ? '— Sélectionner un bien RentalReady —'
                                    : '— Cliquer pour charger les biens RentalReady —'}
                        </option>
                        {/* Conserver l'option du bien lié même avant chargement */}
                        {property.rrRentalId && !rentals.some(r => r.id === property.rrRentalId) && (
                            <option value={property.rrRentalId}>{property.rrRentalName || property.rrRentalId}</option>
                        )}
                        {rentals.map(r => (
                            <option key={r.id} value={r.id}>
                                {r.name}{r.cityName ? ` — ${r.cityName}` : ''}{r.ownerName ? ` (${r.ownerName})` : ''}
                            </option>
                        ))}
                    </select>
                    {rentalsError && (
                        <p className="text-xs text-red-500 mt-1">
                            {rentalsError} — vérifiez la connexion RentalReady dans Administration &gt; Agences.
                        </p>
                    )}
                    {!property.rrRentalId && (
                        <p className="text-xs text-amber-600 mt-1">
                            ⚠️ Sans lien RentalReady, aucun CRG ne pourra être généré pour ce bien.
                        </p>
                    )}
                </div>

                {/* Taux de commission */}
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-1">Taux de commission personnalisé (%)</label>
                        <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.1"
                            className="w-full p-2 border border-slate-300 rounded-lg bg-white text-slate-900 text-sm"
                            placeholder={contractRate != null ? `${contractRate} (hérité du contrat)` : 'Aucun taux au contrat'}
                            value={overrideInput}
                            onChange={e => setOverrideInput(e.target.value)}
                            onBlur={handleOverrideBlur}
                        />
                        <p className="text-xs text-slate-500 mt-1">
                            Laisser vide pour hériter du taux du contrat signé.
                        </p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                        <p className="text-xs font-bold text-slate-500 uppercase mb-1">Taux effectif CRG</p>
                        {effectiveRate != null ? (
                            <p className="text-2xl font-bold text-slate-900">
                                {effectiveRate} %
                                <span className="text-xs font-medium text-slate-500 ml-2">
                                    {property.commissionRateOverride != null ? 'personnalisé' : 'hérité du contrat'}
                                </span>
                            </p>
                        ) : (
                            <p className="text-sm text-red-600 font-medium">
                                Aucun taux disponible : renseignez un taux personnalisé.
                            </p>
                        )}
                    </div>
                </div>

                {/* Règles taxe de séjour par canal */}
                <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <label className="text-sm font-bold text-slate-900">Taxe de séjour par canal</label>
                    <p className="text-xs text-slate-500 mt-0.5 mb-2">
                        « Dans le CRG » : la taxe est comprise dans le versé au client. « Commissionnée » : elle entre dans la base de commission.
                    </p>
                    <table className="w-full">
                        <thead>
                            <tr>
                                <th className="text-left text-[10px] font-bold text-slate-500 uppercase pb-1">Canal</th>
                                <th className="text-center text-[10px] font-bold text-slate-500 uppercase pb-1">Dans le CRG</th>
                                <th className="text-center text-[10px] font-bold text-slate-500 uppercase pb-1">Commissionnée</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(Object.keys(DEFAULT_CITY_TAX_RULES) as CityTaxChannel[]).map(channel => {
                                const effective = {
                                    includeInPayout: property.cityTaxRules?.[channel]?.includeInPayout ?? DEFAULT_CITY_TAX_RULES[channel].includeInPayout,
                                    commissioned: property.cityTaxRules?.[channel]?.commissioned ?? DEFAULT_CITY_TAX_RULES[channel].commissioned,
                                };
                                const updateRule = (patch: Partial<CityTaxRule>) => {
                                    const next: CityTaxRules = {
                                        ...property.cityTaxRules,
                                        [channel]: { ...effective, ...patch },
                                    };
                                    onSave({ ...property, cityTaxRules: next });
                                };
                                const labels: Record<CityTaxChannel, string> = { AIRBNB: 'Airbnb', BOOKING: 'Booking', DIRECT: 'Direct' };
                                return (
                                    <tr key={channel} className="border-t border-slate-200">
                                        <td className="py-1.5 text-sm text-slate-700 font-medium">{labels[channel]}</td>
                                        <td className="py-1.5 text-center">
                                            <input
                                                type="checkbox"
                                                checked={effective.includeInPayout}
                                                onChange={e => updateRule({ includeInPayout: e.target.checked })}
                                                className="w-4 h-4 text-emerald-600 border-slate-300 rounded"
                                            />
                                        </td>
                                        <td className="py-1.5 text-center">
                                            <input
                                                type="checkbox"
                                                checked={effective.commissioned}
                                                onChange={e => updateRule({ commissioned: e.target.checked })}
                                                disabled={!effective.includeInPayout}
                                                title={!effective.includeInPayout ? 'La taxe doit d\'abord être dans le CRG' : undefined}
                                                className="w-4 h-4 text-emerald-600 border-slate-300 rounded disabled:opacity-40"
                                            />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* Refacturation ménage */}
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <div>
                        <label className="text-sm font-bold text-slate-900">Refacturer le ménage au propriétaire</label>
                        <p className="text-xs text-slate-500 mt-0.5">
                            Si activé, les frais de ménage s'ajoutent à la commission dans le total facturé du CRG.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => {
                            const next = !property.rebillCleaning;
                            onSave({ ...property, rebillCleaning: next });
                            toast.success(next ? 'Ménage refacturé au propriétaire' : 'Ménage non refacturé');
                        }}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            property.rebillCleaning ? 'bg-emerald-600' : 'bg-slate-300'
                        }`}
                    >
                        <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                property.rebillCleaning ? 'translate-x-6' : 'translate-x-1'
                            }`}
                        />
                    </button>
                </div>
            </div>
        </div>
    );
};

// Component for Finances Tab
const PropertyFinancesTab: React.FC<{
    property: Property;
    onSaveExpense: (expense: Expense) => Promise<void>;
    onSaveRevenue: (revenue: Revenue) => Promise<void>;
}> = ({ property, onSaveExpense, onSaveRevenue }) => {
    const { toast } = useModal();
    const now = new Date();
    const prevMonth = now.getMonth() === 0 ? 12 : now.getMonth();
    const prevYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
    const [selectedYear, setSelectedYear] = useState(prevYear);
    const [selectedMonth, setSelectedMonth] = useState(prevMonth);
    const [viewMode, setViewMode] = useState<'monthly' | 'table'>('table');
    const [expenses, setExpenses] = useState<Expense[]>([]);
    const [revenues, setRevenues] = useState<Revenue[]>([]);
    const [roi, setRoi] = useState<PropertyROI | null>(null);
    const [loading, setLoading] = useState(true);
    const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);
    const [isRevenueModalOpen, setIsRevenueModalOpen] = useState(false);
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

    useEffect(() => {
        loadFinancialData();
    }, [property.id, selectedYear, selectedMonth]);

    // Fermer le date picker si on clique en dehors
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (!target.closest('.date-picker-container')) {
                setIsDatePickerOpen(false);
            }
        };
        if (isDatePickerOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isDatePickerOpen]);

    const loadFinancialData = async () => {
        if (!property.id) return;
        setLoading(true);
        try {
            // Synchroniser les assets vers les dépenses récurrentes
            await storage.syncAssetsToExpenses(property.id);
            
            const [expData, revData] = await Promise.all([
                storage.getExpenses({ propertyId: property.id, year: selectedYear, month: selectedMonth }),
                storage.getRevenue(property.id, selectedYear, selectedMonth)
            ]);
            setExpenses(expData);
            setRevenues(revData);

            const roiData = await storage.calculatePropertyROI(property.id, selectedYear, selectedMonth);
            setRoi(roiData);
        } catch (error: any) {
            console.error('Error loading financial data:', error);
            toast.error('Erreur lors du chargement des données financières');
        } finally {
            setLoading(false);
        }
    };

    const months = [
        { value: 1, label: 'Janvier' }, { value: 2, label: 'Février' }, { value: 3, label: 'Mars' },
        { value: 4, label: 'Avril' }, { value: 5, label: 'Mai' }, { value: 6, label: 'Juin' },
        { value: 7, label: 'Juillet' }, { value: 8, label: 'Août' }, { value: 9, label: 'Septembre' },
        { value: 10, label: 'Octobre' }, { value: 11, label: 'Novembre' }, { value: 12, label: 'Décembre' }
    ];

    const revenue = revenues[0];
    const totalExpenses = expenses.reduce((sum, e) => sum + e.amount, 0);

    // Navigation entre les mois
    const handlePreviousMonth = () => {
        if (selectedMonth === 1) {
            setSelectedMonth(12);
            setSelectedYear(selectedYear - 1);
        } else {
            setSelectedMonth(selectedMonth - 1);
        }
    };

    const handleNextMonth = () => {
        if (selectedMonth === 12) {
            setSelectedMonth(1);
            setSelectedYear(selectedYear + 1);
        } else {
            setSelectedMonth(selectedMonth + 1);
        }
    };

    const currentMonthLabel = months.find(m => m.value === selectedMonth)?.label || '';

    return (
        <div className="space-y-6">
            {/* Period Selector - Version propre avec sélection */}
            <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        {/* Bouton précédent */}
                        <button
                            onClick={handlePreviousMonth}
                            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                            title="Mois précédent"
                        >
                            <ChevronLeft className="w-5 h-5 text-slate-600" />
                        </button>

                        {/* Sélecteur de date cliquable */}
                        <div className="relative date-picker-container">
                            <button
                                onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
                                className="px-4 py-2 rounded-lg border border-slate-300 hover:border-amber-400 hover:bg-amber-50 transition-colors bg-white min-w-[180px]"
                            >
                                <div className="text-center">
                                    <div className="text-lg font-bold text-slate-900">{currentMonthLabel}</div>
                                    <div className="text-sm text-slate-500">{selectedYear}</div>
                                </div>
                            </button>

                            {/* Dropdown de sélection */}
                            {isDatePickerOpen && (
                                <div className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-lg border border-slate-200 p-4 z-50 min-w-[280px]">
                                    {/* Header avec fermeture */}
                                    <div className="flex justify-between items-center mb-4 pb-2 border-b border-slate-200">
                                        <span className="text-sm font-bold text-slate-700">Sélectionner la période</span>
                                        <button
                                            onClick={() => setIsDatePickerOpen(false)}
                                            className="p-1 hover:bg-slate-100 rounded"
                                        >
                                            <X className="w-4 h-4 text-slate-400" />
                                        </button>
                                    </div>

                                    {/* Sélection du mois */}
                                    <div className="mb-4">
                                        <label className="text-xs font-bold text-slate-500 mb-2 block uppercase">Mois</label>
                                        <div className="grid grid-cols-3 gap-2">
                                            {months.map(month => (
                                                <button
                                                    key={month.value}
                                                    onClick={() => {
                                                        setSelectedMonth(month.value);
                                                        setIsDatePickerOpen(false);
                                                    }}
                                                    className={`p-2 rounded-lg text-sm font-medium transition-colors ${
                                                        selectedMonth === month.value
                                                            ? 'bg-amber-500 text-white shadow-sm'
                                                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                                    }`}
                                                >
                                                    {month.label.substring(0, 3)}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Sélection de l'année */}
                                    <div>
                                        <label className="text-xs font-bold text-slate-500 mb-2 block uppercase">Année</label>
                                        <div className="flex items-center space-x-2">
                                            <input
                                                type="number"
                                                min="2020"
                                                max="2100"
                                                className="flex-1 p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none bg-white text-slate-900 text-sm text-center font-medium"
                                                value={selectedYear}
                                                onChange={(e) => setSelectedYear(parseInt(e.target.value) || prevYear)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        setIsDatePickerOpen(false);
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Bouton suivant */}
                        <button
                            onClick={handleNextMonth}
                            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                            title="Mois suivant"
                        >
                            <ChevronRight className="w-5 h-5 text-slate-600" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Vue toggle et Actions */}
            <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-2 bg-slate-100 p-1 rounded-lg">
                    <button
                        onClick={() => setViewMode('table')}
                        className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
                            viewMode === 'table'
                                ? 'bg-white text-slate-900 shadow-sm'
                                : 'text-slate-600 hover:text-slate-900'
                        }`}
                    >
                        <BarChart3 className="w-4 h-4 inline mr-2" />
                        Vue Tableau
                    </button>
                    <button
                        onClick={() => setViewMode('monthly')}
                        className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
                            viewMode === 'monthly'
                                ? 'bg-white text-slate-900 shadow-sm'
                                : 'text-slate-600 hover:text-slate-900'
                        }`}
                    >
                        <Calendar className="w-4 h-4 inline mr-2" />
                        Vue Mensuelle
                    </button>
                </div>
                
                {viewMode === 'monthly' && (
                    <div className="flex space-x-3">
                        <button
                            onClick={() => setIsRevenueModalOpen(true)}
                            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center shadow-sm font-bold text-sm"
                        >
                            <TrendingUp className="w-4 h-4 mr-2" />
                            Nouveau Revenu
                        </button>
                        <button
                            onClick={() => setIsExpenseModalOpen(true)}
                            className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg flex items-center shadow-sm font-bold text-sm"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Nouvelle Dépense
                        </button>
                    </div>
                )}
            </div>

            {/* Vue Tableau */}
            {viewMode === 'table' && !loading && property.id && (
                <ExpensesTableView
                    propertyId={property.id}
                    property={property}
                    year={selectedYear}
                    agencyId={property.agencyId || undefined}
                    onExpenseSaved={loadFinancialData}
                    onRevenueSaved={loadFinancialData}
                />
            )}

            {/* Vue Mensuelle */}
            {viewMode === 'monthly' && (loading ? (
                <div className="bg-white p-12 rounded-xl shadow-sm border border-slate-200 text-center">
                    <Loader className="w-8 h-8 animate-spin text-amber-500 mx-auto mb-4" />
                    <p className="text-slate-500">Chargement des données financières...</p>
                </div>
            ) : (
                <>
                    {/* Financial Summary */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                            <div className="text-xs font-bold text-slate-500 uppercase mb-1">Revenus nets</div>
                            <div className="text-2xl font-bold text-green-600">
                                {revenue?.netRevenue?.toFixed(2) || '0.00'} €
                            </div>
                            {revenue && (
                                <div className="text-xs text-slate-500 mt-1">
                                    Bruts: {revenue.grossRevenue.toFixed(2)} € - Commissions: {revenue.totalCommissions.toFixed(2)} €
                                </div>
                            )}
                        </div>
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                            <div className="text-xs font-bold text-slate-500 uppercase mb-1">Dépenses</div>
                            <div className="text-2xl font-bold text-red-600">
                                {totalExpenses.toFixed(2)} €
                            </div>
                            <div className="text-xs text-slate-500 mt-1">
                                {expenses.length} dépense{expenses.length > 1 ? 's' : ''}
                            </div>
                        </div>
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                            <div className="text-xs font-bold text-slate-500 uppercase mb-1">Commission Placid</div>
                            <div className="text-2xl font-bold text-purple-600">
                                {roi?.totalPlacidCommission?.toFixed(2) || '0.00'} €
                            </div>
                        </div>
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                            <div className="text-xs font-bold text-slate-500 uppercase mb-1">Profit net</div>
                            <div className={`text-2xl font-bold ${(roi?.netProfit || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {roi?.netProfit?.toFixed(2) || '0.00'} €
                            </div>
                            {roi && roi.totalExpenses > 0 && (
                                <div className="text-xs text-slate-500 mt-1">
                                    ROI: {roi.roiPercentage.toFixed(1)}%
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Detailed Breakdown */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Revenues */}
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                            <h3 className="font-bold text-lg mb-4 flex items-center text-green-700">
                                <TrendingUp className="w-5 h-5 mr-2" /> Revenus du mois
                            </h3>
                            {revenue ? (
                                <div className="space-y-3">
                                    <div className="flex justify-between">
                                        <span className="text-sm text-slate-600">Prix nuitées totales</span>
                                        <span className="font-medium">{revenue.nightlyRatesTotal.toFixed(2)} €</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-slate-600">Taxes de séjour</span>
                                        <span className="font-medium">{revenue.taxesCollected.toFixed(2)} €</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-slate-600">Frais de ménage collectés</span>
                                        <span className="font-medium">{revenue.cleaningFeesCollected.toFixed(2)} €</span>
                                    </div>
                                    <div className="border-t pt-3 mt-3">
                                        <div className="flex justify-between text-red-600">
                                            <span className="text-sm">Commissions plateformes</span>
                                            <span className="font-medium">-{revenue.totalCommissions.toFixed(2)} €</span>
                                        </div>
                                    </div>
                                    <div className="border-t pt-3 mt-3">
                                        <div className="flex justify-between">
                                            <span className="font-bold">Revenus nets</span>
                                            <span className="font-bold text-green-600 text-lg">{revenue.netRevenue.toFixed(2)} €</span>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <p className="text-slate-400 italic text-sm">Aucun revenu enregistré pour ce mois</p>
                            )}
                        </div>

                        {/* Expenses */}
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                            <h3 className="font-bold text-lg mb-4 flex items-center text-red-700">
                                <Euro className="w-5 h-5 mr-2" /> Dépenses du mois
                            </h3>
                            {expenses.length > 0 ? (
                                <div className="space-y-2 max-h-64 overflow-y-auto">
                                    {expenses.map(expense => (
                                        <div key={expense.id} className="flex justify-between items-center p-2 hover:bg-slate-50 rounded">
                                            <div className="flex-1">
                                                <div className="text-sm font-medium text-slate-900">{expense.category}</div>
                                                {expense.description && (
                                                    <div className="text-xs text-slate-500">{expense.description}</div>
                                                )}
                                            </div>
                                            <div className="text-sm font-bold text-slate-900">{expense.amount.toFixed(2)} €</div>
                                        </div>
                                    ))}
                                    <div className="border-t pt-2 mt-2">
                                        <div className="flex justify-between">
                                            <span className="font-bold">Total</span>
                                            <span className="font-bold text-red-600">{totalExpenses.toFixed(2)} €</span>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <p className="text-slate-400 italic text-sm">Aucune dépense enregistrée pour ce mois</p>
                            )}
                        </div>
                    </div>
                </>
            ))}

            {/* Modals */}
            {isExpenseModalOpen && (
                <AddExpenseModal
                    isOpen={isExpenseModalOpen}
                    onClose={() => setIsExpenseModalOpen(false)}
                    onSave={async (expense) => {
                        await onSaveExpense(expense);
                        await loadFinancialData();
                    }}
                    propertyId={property.id || undefined}
                    properties={[property]}
                    agencyId={property.agencyId || undefined}
                    year={selectedYear}
                    month={selectedMonth}
                />
            )}

            {isRevenueModalOpen && (
                <AddRevenueModal
                    isOpen={isRevenueModalOpen}
                    onClose={() => setIsRevenueModalOpen(false)}
                    onSave={async (revenue) => {
                        await onSaveRevenue(revenue);
                        await loadFinancialData();
                    }}
                    propertyId={property.id || undefined}
                    properties={[property]}
                    agencyId={property.agencyId || undefined}
                    year={selectedYear}
                    month={selectedMonth}
                />
            )}
        </div>
    );
};

